/**
 * subscriptionService.js
 */

import { writeFetch, getSession } from '../api';
import { fetchActivePlans, formatStorage } from './plansService';

export { formatStorage };

const TABLE = 'user_subscriptions_1776390702594';

const FALLBACK_TIERS = [
  {
    id: 'free', plan_key: 'free', name: 'Free',
    storage_mb: 100, price_usd: 0, billing_cycle: 'forever', billing: 'forever',
    color: 'from-gray-400 to-gray-500', badge: 'bg-gray-100 text-gray-600 border-gray-200',
    highlight: false,
    features: ['100 MB secure storage', 'Up to 5 documents', 'AES-256 encryption', 'Basic access control'],
  },
  {
    id: 'starter', plan_key: 'starter', name: 'Starter',
    storage_mb: 1024, price_usd: 4.99, billing_cycle: 'month', billing: 'month',
    color: 'from-blue-500 to-blue-700', badge: 'bg-blue-50 text-blue-700 border-blue-200',
    highlight: false,
    features: ['1 GB secure storage', 'Unlimited documents', 'AES-256 encryption', 'Multi-party authorization', 'Priority support'],
  },
  {
    id: 'family', plan_key: 'family', name: 'Family',
    storage_mb: 5120, price_usd: 12.99, billing_cycle: 'month', billing: 'month',
    color: 'from-amber-400 to-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200',
    highlight: true,
    features: ['5 GB secure storage', 'Unlimited documents', 'AES-256 encryption', 'Multi-party authorization', 'Document expiry alerts', 'Priority support'],
  },
  {
    id: 'legacy', plan_key: 'legacy', name: 'Legacy',
    storage_mb: 20480, price_usd: 29.99, billing_cycle: 'month', billing: 'month',
    color: 'from-purple-600 to-indigo-900', badge: 'bg-purple-50 text-purple-700 border-purple-200',
    highlight: false,
    features: ['20 GB secure storage', 'Unlimited documents', 'AES-256 encryption', 'Multi-party authorization', 'Document expiry alerts', 'Dedicated account manager', 'White-glove onboarding'],
  },
];

export let STORAGE_TIERS = [...FALLBACK_TIERS];

function normalizePlanToTier(p) {
  let features = p.features;
  if (typeof features === 'string' && features.trim()) {
    try { features = JSON.parse(features); } catch {
      features = features.split('\n').map(f => f.trim()).filter(Boolean);
    }
  }
  if (!Array.isArray(features)) features = [`${formatStorage(p.storage_mb)} secure storage`];

  return {
    ...p,
    id: p.plan_key,
    billing: p.billing_cycle,
    features,
  };
}

export function getTierById(id) {
  return STORAGE_TIERS.find(t => t.plan_key === id || t.id === id) || STORAGE_TIERS[0];
}

export async function loadStorageTiers() {
  try {
    const plans = await fetchActivePlans();
    if (Array.isArray(plans) && plans.length > 0) {
      // Filter out Will Plans so they don't break DocuVault
      const docuVaultPlans = plans.filter(p => !p.plan_key.startsWith('will_'));
      STORAGE_TIERS = docuVaultPlans.map(normalizePlanToTier);
    } else {
      STORAGE_TIERS = [...FALLBACK_TIERS];
    }
  } catch (err) {
    STORAGE_TIERS = [...FALLBACK_TIERS];
  }
  return STORAGE_TIERS;
}

export async function fetchSubscription() {
  const user = getSession();
  if (!user) return null;
  try {
    const res = await writeFetch(
      `read/${TABLE}?owner_id=${user.id}&status=active&sort=created_at&order=desc&limit=1`,
    );
    const data = await res.json();
    return Array.isArray(data.data) && data.data.length > 0 ? data.data[0] : null;
  } catch {
    return null;
  }
}

export async function activatePlan(tier) {
  const user = getSession();
  if (!user) throw new Error('Not authenticated');

  try {
    const existing = await writeFetch(`read/${TABLE}?owner_id=${user.id}&status=active&limit=10`);
    const exData = await existing.json();
    if (Array.isArray(exData.data)) {
      await Promise.all(
        exData.data.map(sub =>
          writeFetch(`update/${TABLE}/${sub.id}`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'cancelled' }),
          }),
        ),
      );
    }
  } catch { /* silent */ }

  const res = await writeFetch(`create/${TABLE}`, {
    method: 'POST',
    body: JSON.stringify({
      owner_id: user.id,
      plan_id: String(tier.plan_key || tier.id || ''),
      plan_name: String(tier.name || ''),
      storage_limit_mb: Number(tier.storage_mb),
      price_usd: parseFloat(Number(tier.price_usd ?? 0).toFixed(2)),
      billing_cycle: String(tier.billing_cycle || tier.billing || 'month'),
      status: 'active',
    }),
  });
  return res.json();
}

export function computeUsedMb(docs) {
  const bytes = docs.reduce((sum, d) => sum + (d.file_size || 0), 0);
  return parseFloat((bytes / (1024 * 1024)).toFixed(2));
}

export function getFreeTierStorageMb() {
  return STORAGE_TIERS.find(t => t.price_usd === 0)?.storage_mb ?? 100;
}

export function getPlanStorageMb(planKey) {
  return STORAGE_TIERS.find(t => t.plan_key === planKey || t.id === planKey)?.storage_mb ?? 0;
}